"use client";
import { useEffect, useState } from 'react';

interface FontPreloaderWithWaitProps {
  fontFamilies: string[];
  waitForLoad?: boolean; // If true, will wait for fonts to load
  onFontsLoaded?: () => void;
}

/**
 * Extended version of FontPreloader that can optionally WAIT for fonts to load
 * Used ONLY on resume-builder page to prevent CLS
 * Dashboard continues to use the regular FontPreloader (no waiting)
 */
export const FontPreloaderWithWait: React.FC<FontPreloaderWithWaitProps> = ({ 
  fontFamilies, 
  waitForLoad = false,
  onFontsLoaded,
}) => {
  const [fontsReady, setFontsReady] = useState(!waitForLoad);

  useEffect(() => {
    const preloadedFonts = new Set<string>();
    
    // Preload fonts by injecting <link> tags
    // NOTE: HTML Resume iframe (ResumeIFrame.tsx) only uses Regular + Bold, NOT Italic
    // Italic variants are ONLY used by React PDF (Font.register in hooks.tsx)
    // So we should NOT preload Italic here to avoid unnecessary downloads
    fontFamilies.forEach(fontFamily => {
      // Always load Regular and Bold (used by HTML Resume iframe)
      const variants: string[] = ['Regular', 'Bold'];
      
      variants.forEach(variant => {
        const fontUrl = `/fonts/${fontFamily}-${variant}.ttf`;
        
        if (!preloadedFonts.has(fontUrl)) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'font';
          link.type = 'font/ttf';
          link.href = fontUrl;
          link.crossOrigin = 'anonymous';
          
          document.head.appendChild(link);
          preloadedFonts.add(fontUrl);
        }
      });
    });

    // If waitForLoad is true, use CSS Font Loading API to detect when ready
    if (waitForLoad && typeof document !== 'undefined' && 'fonts' in document) {
      const checkFonts = async () => {
        try {
          // Check only Regular and Bold (HTML Resume iframe doesn't use Italic)
          const fontChecks = fontFamilies.flatMap(fontFamily => [
            document.fonts.load(`400 16px ${fontFamily}`), // Regular
            document.fonts.load(`700 16px ${fontFamily}`), // Bold
          ]);

          // Wait for fonts with 3 second timeout
          await Promise.race([
            Promise.all(fontChecks),
            new Promise(resolve => setTimeout(resolve, 3000))
          ]);

          setFontsReady(true);
          onFontsLoaded?.();
        } catch (error) {
          console.warn('Font loading timeout or error:', error);
          setFontsReady(true); // Proceed anyway
          onFontsLoaded?.();
        }
      };

      checkFonts();
    }

    return () => {
      // Cleanup if needed
    };
  }, [fontFamilies, waitForLoad, onFontsLoaded]);

  return null;
};

/**
 * Hook to check if specific font families are loaded
 * Returns true when all fonts are ready
 */
export const useFontsLoaded = (fontFamilies: string[]): boolean => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) {
      setLoaded(true);
      return;
    }

    const checkFonts = async () => {
      try {
        const fontChecks = fontFamilies.flatMap(fontFamily => [
          document.fonts.load(`400 16px ${fontFamily}`),
          document.fonts.load(`700 16px ${fontFamily}`),
        ]);

        await Promise.race([
          Promise.all(fontChecks),
          new Promise(resolve => setTimeout(resolve, 3000))
        ]);

        setLoaded(true);
      } catch (error) {
        console.warn('Font loading error:', error);
        setLoaded(true);
      }
    };

    checkFonts();
  }, [fontFamilies]);

  return loaded;
};
